import React, { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/browser";
import axios from "axios";
import Icon from "../Icon";
import { ValidationError } from "../types";
import { runWindowOpen } from "../helpers";
import { useToast } from "../Toast";
import ValidationErrorBanner from "./ValidationErrorBanner";

import { PAYMENT_STATEMENT_TEMPLATE_API_ENDPOINT } from "../../../helpers/constants";

const SHOW_ITD = false;
const ACCEPT = ".xlsx,.xls";
const MAX_BYTES = 25 * 1024 * 1024;

interface Props {
  onUpload: (payload: { file: File; isInceptionToDate: boolean }) => void;
  uploading: boolean;
  error?: ValidationError | null;
  onClearError: () => void;
  onOpenRecord: (publicId: string) => void;
}

const UploadCard: React.FC<Props> = ({
  onUpload,
  uploading,
  error,
  onClearError,
  onOpenRecord,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<ValidationError | null>(null);
  const [drag, setDrag] = useState(false);
  const [isInceptionToDate, setIsInceptionToDate] = useState(false);
  const [templateUrl, setTemplateUrl] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const shownError = localError || error || null;

  useEffect(() => {
    axios
      .get(PAYMENT_STATEMENT_TEMPLATE_API_ENDPOINT)
      .then((response: { data: Record<string, string> }) => {
        setTemplateUrl(response.data?.bulk_upload_payment_statement || null);
      })
      .catch((exception: unknown) => {
        Sentry.captureException(exception);
      });
  }, []);

  useEffect(() => {
    if (!uploading) return undefined;

    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploading]);

  const wasUploading = useRef(false);
  useEffect(() => {
    if (wasUploading.current && !uploading && !error) {
      setFile(null);
      setLocalError(null);
      if (inputRef.current) inputRef.current.value = "";
    }
    wasUploading.current = uploading;
  }, [uploading, error]);

  const clearFile = (): void => {
    setFile(null);
    setLocalError(null);
    onClearError();
    if (inputRef.current) inputRef.current.value = "";
  };

  const downloadTemplate = (): void => {
    if (!templateUrl) {
      toast.push({
        kind: "warn",
        title: "Template not ready",
        desc: "We couldn't fetch the template URL. Try again in a moment.",
      });
      return;
    }
    window.open(templateUrl, "_blank", "noopener,noreferrer");
  };

  const reject = (message: string, title: string): void => {
    setLocalError({ code: "parse_failed", message } as ValidationError);
    toast.push({ kind: "error", title: title, desc: message });
  };

  const validateAndSet = (incoming: File | null | undefined): void => {
    if (!incoming || uploading) return;

    setLocalError(null);
    onClearError();

    const extension = (incoming.name.split(".").pop() || "").toLowerCase();
    if (!["xlsx", "xls"].includes(extension)) {
      setFile(incoming);
      reject(
        `That file type isn’t supported (.${
          extension || "?"
        }). Please upload an Excel file (.xlsx or .xls).`,
        "That file type isn’t supported",
      );
      return;
    }

    if (incoming.size > MAX_BYTES) {
      setFile(incoming);
      reject(
        `File is too big (${(incoming.size / 1024 / 1024).toFixed(
          1,
        )} MB). The largest file we can take is 25 MB.`,
        "File is too big",
      );
      return;
    }

    setFile(incoming);
  };

  const submit = (): void => {
    if (!file || uploading || shownError) return;
    onUpload({ file, isInceptionToDate });
  };

  const sizeKB = file ? `${(file.size / 1024).toFixed(0)} KB` : "";

  return (
    <div className="card">
      <div className="card-head">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 36,
              height: 36,
              background: "var(--info-bg)",
              color: "var(--info)",
              borderRadius: 8,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Icon name="upload" size={18} />
          </div>
          <div>
            <div className="card-title">Upload a payment statement</div>
            <div className="card-sub">
              Drop an Excel file with your contributions. We’ll check it and
              reconcile it automatically{" "}
              <strong>
                {runWindowOpen()
                  ? "as soon as it’s checked"
                  : "this evening at 7:00 PM"}
              </strong>
              .
            </div>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div
          className={`drop ${drag ? "is-drag" : ""} ${file ? "has-file" : ""}`}
          onDragOver={(event) => {
            event.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDrag(false);
            validateAndSet(event.dataTransfer.files?.[0]);
          }}
        >
          <div className="drop-icon">
            <Icon name={file ? "file-spreadsheet" : "cloud-upload"} size={26} />
          </div>
          <div className="drop-text">
            {!file ? (
              <>
                <div className="drop-title">
                  Drop your file here, or{" "}
                  <span
                    className="link"
                    onClick={() => inputRef.current?.click()}
                  >
                    browse
                  </span>
                </div>
                <div className="drop-sub">
                  Excel files only (.xlsx, .xls) · up to 25 MB · One file can
                  cover multiple products
                </div>
                <div
                  className="drop-sub"
                  style={{ display: "flex", alignItems: "center", gap: 4 }}
                >
                  <Icon name="download" size={12} />
                  <span>
                    New here?{" "}
                    <span className="link" onClick={downloadTemplate}>
                      Download the template
                    </span>{" "}
                    to see the required columns.
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="drop-title">{file.name}</div>
                <div className="drop-sub">{sizeKB}</div>
              </>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            data-testid="statement-file-input"
            style={{ display: "none" }}
            onChange={(event) => validateAndSet(event.target.files?.[0])}
          />
        </div>

        {SHOW_ITD && (
          <label
            htmlFor="itd-checkbox"
            style={{
              marginTop: 14,
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 14px",
              borderRadius: "var(--radius)",
              border: isInceptionToDate
                ? "1.5px solid var(--primary)"
                : "1.5px solid var(--border)",
              background: isInceptionToDate
                ? "color-mix(in srgb, var(--primary) 8%, transparent)"
                : "var(--surface-2)",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <input
              type="checkbox"
              id="itd-checkbox"
              checked={isInceptionToDate}
              onChange={(event) => setIsInceptionToDate(event.target.checked)}
            />
            <span>This file covers contributions since inception</span>
          </label>
        )}

        {shownError && (
          <div style={{ marginTop: 14 }}>
            <ValidationErrorBanner
              error={shownError}
              onOpenRecord={onOpenRecord}
              onRemove={clearFile}
            />
          </div>
        )}

        {file && !shownError && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginTop: 14,
            }}
          >
            <div style={{ color: "var(--muted)", fontSize: 13 }}>
              We’ll check the file as soon as you upload it. You can leave this
              page while it runs.
            </div>
            <div style={{ flex: 1 }} />
            <button className="btn" onClick={clearFile} disabled={uploading}>
              Remove
            </button>
            <button
              className="btn btn-primary"
              onClick={submit}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <span className="ta-spin sm" /> Uploading…
                </>
              ) : (
                <>
                  <Icon name="upload" size={15} /> Upload
                </>
              )}
            </button>
          </div>
        )}

        <div className="hint" style={{ marginTop: 14 }}>
          <Icon
            name="info"
            size={12}
            style={{ verticalAlign: "-2px", marginRight: 4 }}
          />
          You don’t need to enter products or periods — we take them from the
          file when we check it.
        </div>
      </div>
    </div>
  );
};

export default UploadCard;
